import { Bullet, Circle, Label, Scrollbar, Tooltip, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  DateAxis,
  SmoothedXLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { forIn } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class AreaChart extends BaseChart {
  interval!: time.ITimeInterval;

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;

    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        paddingRight: 20,
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        pinchZoomX: true,
        layout: this.layout,
      })
    );

    this.createAxes();
    this.createCursor();

    const series = [this.settings.yAxis];

    this.createSeries(this.settings.yAxis);
    this.showLegend(series, (this.chart as XYChart).series.values as LegendData);
  }

  protected createSeries(seriesName: string) {
    const series = (this.chart as XYChart).series.push(
      SmoothedXLineSeries.new(this.root, {
        name: seriesName,
        xAxis: this.axes.xAxis,
        yAxis: this.axes.yAxis,
        valueYField: seriesName,
        valueXField: this.settings.xAxis,
        minBulletDistance: 10,
        tooltip: Tooltip.new(this.root, {
          labelText: "{valueY}",
        }),
      })
    );

    series.bullets.push(() => {
      return Bullet.new(this.root, {
        locationY: 0,
        sprite: Circle.new(this.root, {
          radius: 4,
          fill: series.get("fill"),
        }),
      });
    });
    const formatterConfig = this.getDataLabelsFormatterConfig();
    const metricKey = this.settings.yAxis;
    const metricFormatter = this.formatters?.[metricKey];
    const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

    series.bullets.push(() => {
      const label = Label.new(this.root, {
        ...chartFontSettings.label,
        centerY: p50,
        dy: -16,
        dx: -40,
        populateText: true,
        fontSize: labelFontSize,
        text: "{valueY}",
      });

      label.adapters.add("text", (labelText, target) => {
        const rawValue = Number(target?.dataItem?.get("valueY" as never));

        if (!Number.isFinite(rawValue) || rawValue === 0) {
          return "";
        }

        if (!this.settings.showDataLabels) {
          return "";
        }

        return metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, this.getMetricFormatter(metricKey, formatterConfig));
      });

      label.adapters.add("visible", (visible) => {
        return this.settings.showDataLabels ? visible : false;
      });

      return Bullet.new(this.root, {
        locationY: 1,
        sprite: label,
      });
    });

    (this.chart as XYChart).set(
      "scrollbarX",
      Scrollbar.new(this.root, {
        orientation: "horizontal",
        marginBottom: 25,
      })
    );

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          param.push({ key, value });
        });
      }
      return param;
    });

    series.fills.template.setAll({
      visible: true,
      fillOpacity: 0.2,
    });

    this.createLegendLabel(series, seriesName);
    this.updateSeriesData(seriesName, series);
  }

  protected createAxes() {
    const xRenderer = AxisRendererX.new(this.root, {
      pan: "zoom",
      minGridDistance: 60,
    });
    xRenderer.labels.template.setAll({ ...chartFontSettings.label });

    const xAxis = (this.chart as XYChart).xAxes.push(
      DateAxis.new(this.root, {
        renderer: xRenderer,
        maxDeviation: 0.1,
        groupData: true,
        groupIntervals: [this.interval],
        markUnitChange: false,
        dateFormats: getAxisDateFormat(this.settings.xAxis),
        baseInterval: {
          timeUnit: TIME_UNIT.DAY,
          count: 1,
        },
      })
    );

    if (this.module === PLAN_MODULE && this.settings.xAxis === "week") {
      this.root.events.once("frameended", () => {
        const privateMin = xAxis.getPrivate("min");
        const privateMax = xAxis.getPrivate("max");
        const privateStep = xAxis.getPrivate("step");
        if (
          typeof privateMin === "number" &&
          typeof privateMax === "number" &&
          typeof privateStep === "number" &&
          privateStep > 0
        ) {
          xAxis.set("min", privateMin - privateStep);
          xAxis.set("max", privateMax + privateStep);
          xAxis.set("strictMinMax", true);
        }
      });
    }

    if (this.settings.xAxisTitle) {
      xAxis.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    const yRenderer = AxisRendererY.new(this.root, {
      pan: "zoom",
    });
    yRenderer.labels.template.setAll({ ...chartFontSettings.label });

    const yAxis = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: yRenderer,
        maxDeviation: 0.1,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxis, this.data),
      })
    );

    if (this.module === PLAN_MODULE && ["week", "quarter"].includes(this.settings.xAxis)) {
      const currentExtraMax = Number(yAxis.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (currentExtraMax < requiredExtraMax) {
        yAxis.set("extraMax", requiredExtraMax);
      }

      // Add one extra grid step on top (after axis calculates its scale).
      this.root.events.once("frameended", () => {
        const privateMax = yAxis.getPrivate("max");
        const privateStep = yAxis.getPrivate("step");
        if (typeof privateMax === "number" && typeof privateStep === "number" && privateStep > 0) {
          yAxis.set("max", privateMax + privateStep);
          yAxis.set("strictMinMax", true);
        }
      });
    }

    if (this.settings.yAxisTitle) {
      yAxis.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { xAxis, yAxis };
  }

  protected createCursor() {
    const cursor = (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.axes.xAxis,
      })
    );
    cursor.lineX.set("visible", true);
    cursor.lineY.set("visible", false);
  }

  protected mapData(data: ChartDataRecord[]) {
    const mappedData: ChartDataRecord[] = [];
    if (!Array.isArray(data)) {
      return [];
    }
    data.forEach((el) => {
      mappedData.push({
        [this.settings.xAxis]: el[this.settings.xAxis] === null ? 0 : el[this.settings.xAxis],
        [this.settings.yAxis]: el[this.settings.yAxis] === null ? 0 : el[this.settings.yAxis],
      });
    });
    return mappedData;
  }

  protected updateSeriesData(seriesName: string, series: SmoothedXLineSeries): void {
    const data = [] as ChartDataRecord[];
    // Use this for future data-label tasks
    // const labelEvery = Math.max(1, Number(this.settings?.dataLabelEvery ?? 2));
    this.data.forEach((el: ChartDataRecord) => {
      const date = stringOrNumberToDate(el[this.settings.xAxis] as string).getTime();
      data.push({
        [this.settings.xAxis]: date,
        [seriesName]: el[seriesName],
      });
    });

    // for (let i = 0; i < data.length; i++) {
    //   (data[i] as any).__label = i % labelEvery === 0;
    // }
    // if (data.length) {
    //   (data[data.length - 1] as any).__isLast = true;
    //   (data[data.length - 1] as any).__label = true;
    // }

    series.data.setAll(data);
  }
}
